import {
  FormSchema,
  HandoverFormSchema,
  HandoverProductSchema,
  ProductSchema,
} from "@/lib/form/schema"
import { FormData, FormParamStatus } from "@/lib/form/types"
import {
  OfflineBoiloutBundle,
  OfflineBoiloutSession,
} from "@/boilout/lib/offline-store"
import { EnumsOf } from "@/lib/utils"
interface ShiftProduct {
  id: string
  name: string
}
import { StateCreator } from "@/store/utils"

export const ShiftDialog = {
  None: "None",
  AddProducts: "AddProducts",
} as const

export type ShiftDialog = EnumsOf<typeof ShiftDialog>

export const SessionStatus = {
  Active: "ACTIVE",
  Inactive: "INACTIVE",
  Ended: "ENDED",
} as const
export type SessionStatus = EnumsOf<typeof SessionStatus>

export const initialState: () => BOILOUTState = () => ({
  schemas: {
    self: {
      products: [],
    },
    handovers: [],
  },
  forms: {
    self: {
      products: [],
    },
    handovers: [],
  },
  histories: {},
  formStatus: {
    self: FormParamStatus.PENDING,
    handovers: {},
  },
  fieldMeta: {
    self: {},
    handovers: [],
  },
  filled: {
    self: {},
    handovers: [],
  },
  shift: {
    id: "",
    processId: "",
    shiftId: "",
    products: [],
    currDialog: ShiftDialog.None,
  },
  currLineIdx: 0,
  currProductIdx: 0,
  currHandoverSessionId: "",
  currHandoverIdx: -1,
  lineStatus: {},
  submitLoading: false,
  isReadOnly: false,
  offline: {
    bundle: null,
    activeSession: null,
    hydrated: false,
  },
})

export interface BOILOUTState {
  schemas: {
    self: FormSchema
    handovers: HandoverFormSchema[]
  }
  histories: Record<string, ProductSchema>
  forms: {
    self: FormData
    handovers: FormData[]
  }
  formStatus: {
    self: FormParamStatus
    handovers: Record<string, FormParamStatus>
  }
  filled: {
    self: Record<string, boolean>
    handovers: {
      sessionId: string
      filled: Record<string, boolean>
    }[]
  }
  shift: {
    id: string
    processId: string
    shiftId: string
    products: ShiftProduct[]
    currDialog: ShiftDialog
  }
  currHandoverSessionId: string
  currHandoverIdx: number
  currLineIdx: number
  currProductIdx: number
  lineStatus: Record<string, FormParamStatus | undefined>
  submitLoading: boolean
  isReadOnly: boolean
  offline: {
    bundle: OfflineBoiloutBundle | null
    activeSession: OfflineBoiloutSession["meta"] | null
    hydrated: boolean
  }
}

export type BOILOUTSlice = {
  boilout: BOILOUTState & {
    setSelfSchema(schema: FormSchema): void
    setSelfProductSchema(schema: ProductSchema): void
    addSelfProductSchemas(schemas: ProductSchema[]): void
    removeSelfProductionLine(params: {
      productIdx: number
      lineIdx: number
    }): void
    addProductHistory(schema: ProductSchema): void
    getProductHistory(productId: string): ProductSchema | undefined

    setHandoverSchema(schema: HandoverFormSchema): void
    setHandoverSchemas(schemas: HandoverFormSchema[]): void
    setHandoverProductSchema(
      schema: HandoverProductSchema,
      sessionId: string
    ): void
    setHandoverFormData(data: FormData, sessionId: string): void
    removeHandoverSchema(sessionId: string): void
    removeHandoverProductSchema(schemaId: string, sessionId: string): void

    setShiftDetails(details: Partial<BOILOUTState["shift"]>): void
    clearShift(): void
    clearFormData(): void
    setShiftDialog(dialog: ShiftDialog): void

    setSelfFormData(data: FormData): void
    setCurrLineIdx(idx: number): void
    setCurrProductIdx(idx: number): void
    setCurrHandoverIdx(idx: number): void
    currLineId(): string
    currProdId(): string

    setLineStatus(params: {
      productId: string
      lineId: string
      status: FormParamStatus | undefined
      sessionId?: string
    }): void
    setLineStatuses(statuses: Record<string, FormParamStatus | undefined>): void

    getLineStatus(params: LineStatusKeyParams | string): FormParamStatus
    getCurrLineStatus(): FormParamStatus
    resetLineStatus(
      statuses?: Record<string, FormParamStatus | undefined>
    ): void
    setSubmitLoading(status: boolean): void

    setSelfFormStatus(status: FormParamStatus): void
    setHandoverFormStatus(sessionId: string, status: FormParamStatus): void

    setSelfFilled(filled: Record<string, boolean>): void
    setHandoversFilled(data: BOILOUTState["filled"]["handovers"]): void

    removePendingSheets(): void
    setReadOnlyMode(isReadOnly: boolean): void
    setOfflineBundle(bundle: OfflineBoiloutBundle | null): void
    setOfflineHydrated(hydrated: boolean): void
    startOfflineSession(meta: OfflineBoiloutSession["meta"]): void
    updateOfflineSessionMeta(
      meta: Partial<OfflineBoiloutSession["meta"]> | null
    ): void
    hydrateOfflineSession(session: OfflineBoiloutSession): void
    clearOfflineSession(): void
  }
}

function actionName(action: string) {
  return `boilout/${action}`
}

const createBOILOUTSlice: StateCreator<BOILOUTSlice> = (set, get) => ({
  boilout: {
    ...initialState(),
    setSelfSchema(schema) {
      set(
        (state) => {
          state.boilout.schemas.self = schema

          return state
        },
        undefined,
        actionName("setSelfSchema")
      )
    },
    setSelfProductSchema(schema) {
      set(
        (state) => {
          const foundIdx = state.boilout.schemas.self.products.findIndex(
            (product) => product.id === schema.id
          )

          if (foundIdx < 0) {
            state.boilout.schemas.self.products.push(schema)
          } else {
            state.boilout.schemas.self.products[foundIdx] = schema
          }

          return state
        },
        undefined,
        actionName("setSelfProductSchema")
      )
    },
    addProductHistory(schema) {
      set(
        (state) => {
          state.boilout.histories[schema.id] = schema

          return state
        },
        undefined,
        actionName("addProductHistory")
      )
    },
    getProductHistory(productId) {
      const state = get().boilout

      return state.histories[productId]
    },
    addSelfProductSchemas(schemas) {
      set(
        (state) => {
          state.boilout.schemas.self.products.push(...schemas)

          return state
        },
        undefined,
        actionName("setSelfProductSchemas")
      )
    },
    removeSelfProductionLine(params) {
      set(
        (state) => {
          if (params.lineIdx < 0 || params.productIdx < 0) {
            return state
          }

          const boilout = state.boilout
          const self = state.boilout.schemas.self
          const product = self.products[params.productIdx]
          const forms = boilout.forms.self
          const line = product.productionLines[params.lineIdx]

          // Delete the line status by ID (no re-indexing needed)
          const lineStatusKey = toLineStatusKey({
            productId: product.id,
            lineId: line.id,
            sessionId: "self",
          })
          delete state.boilout.lineStatus[lineStatusKey]

          if (product.productionLines.length < 2) {
            // Removing the last line means removing the entire product
            self.products.splice(params.productIdx, 1)
            forms.products.splice(params.productIdx, 1)

            if (params.productIdx !== 0) {
              boilout.currProductIdx = params.productIdx - 1
            }

            boilout.currLineIdx = 0
          } else {
            product.productionLines.splice(params.lineIdx, 1)
            forms.products[params.productIdx].productionLines.splice(
              params.lineIdx,
              1
            )

            if (boilout.currLineIdx > 0) {
              boilout.currLineIdx -= 1
            }
          }

          return state
        },
        undefined,
        actionName("removeSelfProductionLine")
      )
    },

    setHandoverSchema(schema) {
      set(
        (state) => {
          const foundIdx = state.boilout.schemas.handovers.findIndex(
            (handover) =>
              handover.handoverFromEmployeeSessionId ===
              schema.handoverFromEmployeeSessionId
          )

          if (foundIdx < 0) {
            state.boilout.schemas.handovers.push(schema)
            state.boilout.forms.handovers.push({
              handoverFromEmployeeCode: schema.handoverFromEmployeeCode,
              handoverFromEmployeeSessionId:
                schema.handoverFromEmployeeSessionId,
              products: [],
            })
          } else {
            state.boilout.schemas.handovers[foundIdx] = schema
          }

          return state
        },
        undefined,
        actionName("setHandoverSchema")
      )
    },
    setHandoverSchemas(schemas) {
      set(
        (state) => {
          state.boilout.schemas.handovers = schemas
          state.boilout.forms.handovers = schemas.map((schema) => {
            return {
              handoverFromEmployeeSessionId:
                schema.handoverFromEmployeeSessionId,
              handoverFromEmployeeCode: schema.handoverFromEmployeeCode,
              products: [],
            }
          })
          state.boilout.filled.handovers = schemas.map((schema) => {
            return {
              sessionId: schema.handoverFromEmployeeSessionId,
              filled: {},
            }
          })

          return state
        },
        undefined,
        actionName("setHandoverSchemas")
      )
    },
    removeHandoverSchema(sessionId) {
      set(
        (state) => {
          state.boilout.schemas.handovers = state.boilout.schemas.handovers.filter(
            (handover) => {
              return handover.handoverFromEmployeeSessionId !== sessionId
            }
          )

          return state
        },
        undefined,
        actionName("removeHandoverSchema")
      )
    },
    setHandoverProductSchema(schema, sessionId) {
      set(
        (state) => {
          let schemaFound: ProductSchema | null = null

          const handoverIdx = state.boilout.schemas.handovers.findIndex(
            (handover) => handover.handoverFromEmployeeSessionId === sessionId
          )

          if (handoverIdx < 0) {
            return state
          }

          const handover = state.boilout.schemas.handovers[handoverIdx]
          const schemaIdx = handover.products.findIndex(
            (product) => product.id === schema.id
          )

          if (schemaIdx > -1) {
            schemaFound = handover.products[schemaIdx]
          }

          if (schemaFound) {
            state.boilout.schemas.handovers[handoverIdx].products[schemaIdx] =
              schema
          } else {
            state.boilout.schemas.handovers[handoverIdx].products.push(schema)
          }

          return state
        },
        undefined,
        actionName("setHandoverProductSchema")
      )
    },
    removeHandoverProductSchema(schemaId, sessionId) {
      set(
        (state) => {
          const handoverIdx = state.boilout.schemas.handovers.findIndex(
            (handover) => handover.handoverFromEmployeeSessionId === sessionId
          )

          if (handoverIdx < 0) {
            return state
          }

          const handover = state.boilout.schemas.handovers[handoverIdx]
          handover.products = handover.products.filter(
            (product) => product.id !== schemaId
          )

          return state
        },
        undefined,
        actionName("removeHandoverProductSchema")
      )
    },
    setHandoverFormData(data, sessionId) {
      set(
        (state) => {
          const foundIndex = state.boilout.forms.handovers.findIndex(
            (handover) => {
              return handover.handoverFromEmployeeSessionId === sessionId
            }
          )

          if (foundIndex < 0) {
            state.boilout.forms.handovers.push(data)

            return state
          }

          state.boilout.forms.handovers = state.boilout.forms.handovers.map(
            (handover) => {
              if (handover.handoverFromEmployeeSessionId === sessionId) {
                return {
                  ...handover,
                  ...data,
                }
              }

              return handover
            }
          )

          return state
        },
        undefined,
        actionName("setHandoverFormData")
      )
    },

    setShiftDetails(details) {
      set(
        (state) => {
          state.boilout.shift = {
            ...state.boilout.shift,
            ...details,
          }

          return state
        },

        undefined,
        actionName("setShiftDetails")
      )
    },
    clearShift() {
      set(
        (state) => {
          const bundle = state.boilout.offline.bundle
          const hydrated = state.boilout.offline.hydrated
          state.session.session.processes =
            state.session.session.processes.filter(
              (proc) => proc.processId !== state.boilout.shift.processId
            )

          state.boilout = {
            ...state.boilout,
            ...initialState(),
            offline: {
              bundle,
              activeSession: null,
              hydrated,
            },
          }

          return state
        },
        undefined,
        actionName("clearShift")
      )
    },
    clearFormData() {
      set(
        (state) => {
          const initial = initialState()
          state.boilout.schemas = initial.schemas
          state.boilout.forms = initial.forms
          state.boilout.lineStatus = {}
          state.boilout.histories = {}
          state.boilout.filled = initial.filled
          state.boilout.currLineIdx = 0
          state.boilout.currProductIdx = 0
          state.boilout.currHandoverIdx = -1
          state.boilout.currHandoverSessionId = ""

          return state
        },
        undefined,
        actionName("clearFormData")
      )
    },
    setShiftDialog(dialog) {
      set(
        (state) => {
          state.boilout.shift.currDialog = dialog

          return state
        },
        undefined,
        actionName("setShiftDialog")
      )
    },

    setSelfFormData(data) {
      set(
        (state) => {
          state.boilout.forms.self = data

          return state
        },
        undefined,
        actionName("setSelfFormData")
      )
    },
    setLineStatus({ status, ...params }) {
      set(
        (state) => {
          state.boilout.lineStatus[toLineStatusKey(params)] = status

          return state
        },
        undefined,
        actionName("setLineStatus")
      )
    },
    setLineStatuses(statuses) {
      set(
        (state) => {
          state.boilout.lineStatus = statuses

          return state
        },
        undefined,
        actionName("setLineStatuses")
      )
    },
    getLineStatus(params) {
      const store = get()

      return (
        store.boilout.lineStatus[
        typeof params === "string" ? params : toLineStatusKey(params)
        ] || FormParamStatus.PENDING
      )
    },
    getCurrLineStatus() {
      const store = get()

      const status =
        store.boilout.lineStatus[
        toLineStatusKey({
          productId: this.currProdId(),
          lineId: this.currLineId(),
          sessionId: store.boilout.currHandoverSessionId || "self",
        })
        ]

      return status || FormParamStatus.PENDING
    },
    resetLineStatus(statuses) {
      set(
        (state) => {
          state.boilout.lineStatus = statuses || {}

          return state
        },
        undefined,
        actionName("resetLineStatus")
      )
    },
    setCurrLineIdx(idx) {
      set(
        (state) => {
          state.boilout.currLineIdx = idx

          return state
        },
        undefined,
        actionName("setCurrLineIdx")
      )
    },
    currLineId() {
      const state = get()

      if (state.boilout.currLineIdx < 0 || state.boilout.currProductIdx < 0) {
        return ""
      }

      if (state.boilout.currHandoverSessionId) {
        if (
          !state.boilout.schemas.handovers.length ||
          !state.boilout.schemas.handovers.at(state.boilout.currHandoverIdx)?.products
            .length ||
          !state.boilout.schemas.handovers
            .at(state.boilout.currHandoverIdx)
            ?.products.at(state.boilout.currProductIdx)?.productionLines.length
        ) {
          return ""
        }

        return (
          state.boilout.schemas.handovers
            .at(state.boilout.currHandoverIdx)
            ?.products.at(state.boilout.currProductIdx)
            ?.productionLines.at(state.boilout.currLineIdx)?.id || ""
        )
      }

      if (
        !state.boilout.schemas.self.products.length ||
        !state.boilout.schemas.self.products.at(state.boilout.currProductIdx)
          ?.productionLines.length
      ) {
        return ""
      }

      return (
        state.boilout.schemas.self.products
          .at(state.boilout.currProductIdx)
          ?.productionLines.at(state.boilout.currLineIdx)?.id || ""
      )
    },
    currProdId() {
      const state = get()

      if (state.boilout.currLineIdx < 0 || state.boilout.currProductIdx < 0) {
        return ""
      }

      if (state.boilout.currHandoverSessionId) {
        if (
          !state.boilout.schemas.handovers.length ||
          !state.boilout.schemas.handovers.at(state.boilout.currHandoverIdx)?.products
            .length
        ) {
          return ""
        }

        return (
          state.boilout.schemas.handovers
            .at(state.boilout.currHandoverIdx)
            ?.products.at(state.boilout.currProductIdx)?.id || ""
        )
      }

      if (!state.boilout.schemas.self.products.length) {
        return ""
      }

      return (
        state.boilout.schemas.self.products.at(state.boilout.currProductIdx)?.id || ""
      )
    },
    setCurrProductIdx(idx) {
      set(
        (state) => {
          state.boilout.currProductIdx = idx

          return state
        },
        undefined,
        actionName("setCurrProductIdx")
      )
    },

    setSubmitLoading(status) {
      set(
        (state) => {
          state.boilout.submitLoading = status

          return state
        },
        undefined,
        actionName("setSubmitLoading")
      )
    },
    setCurrHandoverIdx(idx) {
      set(
        (state) => {
          if (idx < 0 || !state.boilout.schemas.handovers.length) {
            state.boilout.currHandoverSessionId = ""
            state.boilout.currHandoverIdx = -1

            return state
          }

          const handover = state.boilout.schemas.handovers[idx]
          if (!handover) {
            state.boilout.currHandoverSessionId = ""
            state.boilout.currHandoverIdx = -1
            return state
          }

          state.boilout.currHandoverIdx = idx
          state.boilout.currHandoverSessionId = handover.handoverFromEmployeeSessionId

          return state
        },
        undefined,
        actionName("setCurrHandoverIdx")
      )
    },

    setSelfFormStatus(status) {
      set(
        (state) => {
          state.boilout.formStatus.self = status

          return state
        },
        undefined,
        actionName("setSelfFormStatus")
      )
    },
    setHandoverFormStatus(sessionId, status) {
      set(
        (state) => {
          state.boilout.formStatus.handovers[sessionId] = status

          return state
        },
        undefined,
        actionName("setHandoverFormStatus")
      )
    },

    setSelfFilled(filled) {
      set(
        (state) => {
          state.boilout.filled.self = filled

          return state
        },
        undefined,
        "setSelfFilled"
      )
    },
    setHandoversFilled(data) {
      set(
        (state) => {
          state.boilout.filled.handovers = data

          return state
        },
        undefined,
        "setHandoverFilled"
      )
    },
    removePendingSheets() {
      set(
        (state) => {
          const schemaProducts = state.boilout.schemas.self.products

          state.boilout.forms.self.products = state.boilout.forms.self.products.map(
            (product, productIdx) => {
              const schemaProduct = schemaProducts[productIdx]
              product.productionLines = product.productionLines.filter(
                (__line, lineIdx) => {
                  const schemaLine = schemaProduct?.productionLines[lineIdx]
                  return (
                    this.getLineStatus({
                      productId: schemaProduct?.id || "",
                      lineId: schemaLine?.id || "",
                    }) === FormParamStatus.SUBMITTED
                  )
                }
              )

              return product
            }
          )

          state.boilout.forms.self.products =
            state.boilout.forms.self.products.filter(
              (product) => !!product.productionLines.length
            )

          state.boilout.schemas.self.products =
            state.boilout.schemas.self.products.map((product) => {
              product.productionLines = product.productionLines.filter(
                (line) => {
                  return (
                    this.getLineStatus({
                      productId: product.id,
                      lineId: line.id,
                    }) === FormParamStatus.SUBMITTED
                  )
                }
              )

              return product
            })

          state.boilout.schemas.self.products =
            state.boilout.schemas.self.products.filter(
              (product) => !!product.productionLines.length
            )

          return state
        },
        undefined,
        "removePendingSheets"
      )
    },
    setReadOnlyMode(isReadOnly) {
      set(
        (state) => {
          state.boilout.isReadOnly = isReadOnly

          return state
        },
        undefined,
        actionName("setReadOnlyMode")
      )
    },
    setOfflineBundle(bundle) {
      set(
        (state) => {
          state.boilout.offline.bundle = bundle

          if (bundle?.products?.length) {
            state.boilout.shift.products = bundle.products.map((product) => ({
              id: product.id,
              name: product.name,
            }))
          }

          return state
        },
        undefined,
        actionName("setOfflineBundle")
      )
    },
    setOfflineHydrated(hydrated) {
      set(
        (state) => {
          state.boilout.offline.hydrated = hydrated

          return state
        },
        undefined,
        actionName("setOfflineHydrated")
      )
    },
    startOfflineSession(meta) {
      set(
        (state) => {
          state.boilout.offline.activeSession = meta

          return state
        },
        undefined,
        actionName("startOfflineSession")
      )
    },
    updateOfflineSessionMeta(meta) {
      set(
        (state) => {
          if (!meta) {
            state.boilout.offline.activeSession = null

            return state
          }

          state.boilout.offline.activeSession = {
            ...(state.boilout.offline.activeSession || ({} as OfflineBoiloutSession["meta"])),
            ...meta,
          }

          return state
        },
        undefined,
        actionName("updateOfflineSessionMeta")
      )
    },
    hydrateOfflineSession(session) {
      set(
        (state) => {
          state.boilout.schemas.self = session.schema
          state.boilout.forms.self = session.form
          state.boilout.schemas.handovers = session.handoverSchemas || []
          state.boilout.forms.handovers = session.handoverForms || []
          state.boilout.filled.handovers = (session.handoverSchemas || []).map(
            (handover) => ({
              sessionId: handover.handoverFromEmployeeSessionId,
              filled: {},
            })
          )
          state.boilout.lineStatus = session.lineStatus
          state.boilout.currProductIdx = session.currProductIdx
          state.boilout.currLineIdx = session.currLineIdx
          state.boilout.currHandoverIdx = session.currHandoverIdx
          state.boilout.currHandoverSessionId = session.currHandoverSessionId
          state.boilout.offline.activeSession = session.meta
          state.boilout.shift.processId = session.meta.processId
          state.boilout.shift.shiftId = session.meta.shiftId

          if (!state.boilout.shift.products.length) {
            state.boilout.shift.products = session.schema.products.map((product) => ({
              id: product.id,
              name: product.name,
            }))
          }

          return state
        },
        undefined,
        actionName("hydrateOfflineSession")
      )
    },
    clearOfflineSession() {
      set(
        (state) => {
          state.boilout.offline.activeSession = null

          return state
        },
        undefined,
        actionName("clearOfflineSession")
      )
    },
  },
})

type LineStatusKeyParams = {
  productId: string
  lineId: string
  sessionId?: string
}

export function toLineStatusKey(params: LineStatusKeyParams) {
  return `${params.productId}::${params.lineId}::${params.sessionId || "self"}`
}

export function toLineStatusKeyParts(key: string): {
  productId: string
  lineId: string
  sessionId: string
} {
  const parts = key.split("::")

  return {
    productId: parts.at(0) || "",
    lineId: parts.at(1) || "",
    sessionId: parts.at(2) || "",
  }
}

export default createBOILOUTSlice
